import { shortText } from '../print/rules';
import { FONT_CATALOG, FONT_GROUPS, hasThai, THAI_FALLBACK, type FontGroup } from '../fonts/catalog';
import { $, setPressed } from './dom';
import { openSheet } from './sheets';

/*
 * "ฟอนต์" (choice ก): every font in groups, each row showing a sample in that font — the selected text itself when
 * there is one (so a Thai text in an English-only font shows its Thai in the stand-in font, as it will look).
 * Samples get their font only when the row comes into view, so opening the sheet does not download every font.
 * Labels: "อังกฤษเท่านั้น" (no Thai letters), "ไม่มีตัวหนา" (only one weight).
 */
export interface FontSheetHooks {
  /** the selected text's font and words, or null */
  current: () => { family: string; text: string } | null;
  /** use this font for the selected text; a Thai reason when it could not */
  pick: (family: string) => Promise<string | null>;
}

const SAMPLE_TH = 'สวัสดีค่ะ Hello 123';
const SAMPLE_EN = 'Hello Coffee 123';

export function initFontSheet(h: FontSheetHooks) {
  const list = $('fnList'), msg = $('fnMsg');
  let group: FontGroup | '' = '';
  let busy = false;
  const say = (t: string) => { msg.textContent = t; msg.hidden = !t; };

  // a sample's font is set when its row is seen (IntersectionObserver), not when the list is drawn
  const seen = new IntersectionObserver(entries => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      const el = e.target as HTMLElement;
      el.style.fontFamily = `"${el.dataset.family}"`;
      seen.unobserve(el);
    }
  }, { root: list });

  // the group chips are made once; a tap only changes which is pressed (CORE: never rebuild the button being tapped)
  const chips = $('fnChips');
  const chip = (id: FontGroup | '', label: string) => {
    const b = document.createElement('button'); b.className = 'fchip'; b.textContent = label; b.dataset.v = id;
    b.onclick = () => { group = id; setPressed('fnChips', x => x.dataset.v === group); paint(); };
    chips.append(b);
  };
  chip('', `ทั้งหมด ${FONT_CATALOG.length}`);
  for (const g of FONT_GROUPS) chip(g.id, `${g.name} ${FONT_CATALOG.filter(f => f.group === g.id).length}`);

  function paint() {
    const cur = h.current();
    setPressed('fnChips', x => x.dataset.v === group);
    seen.disconnect();
    list.textContent = '';
    const own = cur?.text.split('\n').find(l => l.trim())?.trim();
    for (const g of FONT_GROUPS) {
      if (group && group !== g.id) continue;
      list.append(Object.assign(document.createElement('span'), { className: 'sec', textContent: g.name }));
      for (const f of FONT_CATALOG.filter(x => x.group === g.id)) {
        const b = document.createElement('button');
        b.className = 'fnrow';
        b.dataset.family = f.family;
        b.disabled = busy || !cur;
        b.setAttribute('aria-pressed', String(cur?.family === f.family));
        b.setAttribute('aria-label', `ใช้ฟอนต์ ${f.family}`);
        const top = document.createElement('span'); top.className = 'fntop';
        top.append(Object.assign(document.createElement('b'), { textContent: f.family }));
        const tag = (t: string) => top.append(Object.assign(document.createElement('small'), { className: 'fntag', textContent: t }));
        if (!f.thai) tag('อังกฤษเท่านั้น');
        if (!f.weights.includes(700)) tag('ไม่มีตัวหนา');
        const sample = document.createElement('span');
        sample.className = 'fnsample';
        sample.dataset.family = f.family;
        sample.textContent = own ? shortText(own, 28) : f.thai ? SAMPLE_TH : SAMPLE_EN;
        b.append(top, sample);
        if (own && !f.thai && hasThai(own)) b.append(Object.assign(document.createElement('small'), { className: 'fnnote', textContent: `ตัวไทยใช้ ${THAI_FALLBACK} แทน` }));
        b.onclick = () => void choose(f.family);
        list.append(b);
        seen.observe(sample);
      }
    }
    if (!cur) say('เลือกกล่องข้อความก่อน แล้วค่อยเลือกฟอนต์');
  }

  // the rows stay as they are while a font loads (CORE: never rebuild the button being tapped); only their state changes
  const rows = () => [...list.querySelectorAll<HTMLButtonElement>('button.fnrow')];
  async function choose(family: string) {
    if (busy) return;
    busy = true; say(`กำลังโหลดฟอนต์ ${family}…`);
    for (const b of rows()) b.disabled = true;
    try {
      const why = await h.pick(family);
      say(why ?? '');
    } catch (e) {
      say(`เปลี่ยนฟอนต์ไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      busy = false;
      sync();
    }
  }

  /** the selected text changed (another one chosen, undo): rows keep their place, only their state and sample change */
  function sync() {
    const cur = h.current();
    const own = cur?.text.split('\n').find(l => l.trim())?.trim();
    for (const b of rows()) {
      const family = b.dataset.family!;
      const f = FONT_CATALOG.find(x => x.family === family)!;
      b.disabled = busy || !cur;
      b.setAttribute('aria-pressed', String(cur?.family === family));
      const sample = b.querySelector<HTMLElement>('.fnsample')!;
      sample.textContent = own ? shortText(own, 28) : f.thai ? SAMPLE_TH : SAMPLE_EN;
      const note = b.querySelector('.fnnote');
      const needNote = !!own && !f.thai && hasThai(own);
      if (note && !needNote) note.remove();
      if (!note && needNote) b.append(Object.assign(document.createElement('small'), { className: 'fnnote', textContent: `ตัวไทยใช้ ${THAI_FALLBACK} แทน` }));
    }
    if (!cur && !busy) say('เลือกกล่องข้อความก่อน แล้วค่อยเลือกฟอนต์');   // a result message from picking stays
  }

  return {
    open() { say(''); paint(); openSheet('shFonts'); },
    sync
  };
}
